import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useApolloClient, gql } from '@apollo/client';
import { LuPin } from 'react-icons/lu';
import { message, Spin } from 'antd';
import { get } from 'lodash';
import { motion, AnimatePresence } from 'motion/react';

import { createAnnouncement } from 'src/_shared/api/graphql/custom/announcements/createAnnouncementGraphql.js';
import { updateAnnouncement } from 'src/_shared/api/graphql/custom/announcements/updateAnnouncementGraphql.js';
import { deleteAnnouncement } from 'src/_shared/api/graphql/custom/announcements/deleteAnnouncementGraphql.js';
import { ml } from 'src/_shared/services/utils.js';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';

import { BATCH_SIZE, MODERATOR_ROLES, DEFAULT_ACCENT } from './constants.js';
import { darkenHex } from './utils.js';
import {
	QueryCompanyEntitiesUserGroups,
	GetCompanyAnnouncementsSettings,
	UpdateCompanyAnnouncementsSettings,
	IncrementAnnouncementViewCount,
	QueryAnnouncementsTagsByCompanyId,
	queryAnnouncements,
} from './graphqlQueries.js';
import SettingsModal from './SettingsModal.jsx';
import SearchFilterBar from './SearchFilterBar.jsx';
import AnnouncementCard from './AnnouncementCard.jsx';
import AnnouncementDetail from './AnnouncementDetail.jsx';
import AnnouncementForm from './AnnouncementForm.jsx';

// ─── Main Component ───

function NewAnnouncementsComponent() {
	const currentUser = useSelector((state) => state.user.currentUser);
	const allMultiLingualData = useSelector(
		(state) => state.dashboard.allMultiLingualData
	);
	const client = useApolloClient();

	const [announcements, setAnnouncements] = useState([]);
	const [loading, setLoading] = useState(true);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [filterLoading, setFilterLoading] = useState(false);
	const hasInitiallyLoaded = useRef(false);
	const [userGroups, setUserGroups] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedTags, setSelectedTags] = useState([]);
	const [sortBy, setSortBy] = useState('newest');
	const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [editingAnnouncement, setEditingAnnouncement] = useState(null);
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [preloadedThumbnailUrl, setPreloadedThumbnailUrl] = useState(null);
	const [headerTitle, setHeaderTitle] = useState('');
	const [headerSubtext, setHeaderSubtext] = useState('');
	const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
	const [gradientStart, setGradientStart] = useState('#14b8a6');
	const [gradientMid, setGradientMid] = useState('#34d399');
	const [gradientEnd, setGradientEnd] = useState('#06b6d4');

	const gradientCssLooping = `linear-gradient(in oklab to right, ${gradientStart}, ${gradientMid}, ${gradientEnd}, ${gradientStart})`;
	const [pinnedColor, setPinnedColor] = useState('#fbbf24');
	const [showAuthor, setShowAuthor] = useState(true);
	const [showViews, setShowViews] = useState(false);
	const [showCount, setShowCount] = useState(false);
	const [defaultAuthor, setDefaultAuthor] = useState('');

	const displayAs = get(currentUser, 'displayAs');
	const isModerator = MODERATOR_ROLES.includes(displayAs);
	const languageCode = get(currentUser, 'languageCode');

	// Fetch announcements
	const [totalCount, setTotalCount] = useState(0);
	const [nextToken, setNextToken] = useState(null);

	const fetchAnnouncementsNew = useCallback(
		async (afterCursor = null) => {
			if (!currentUser?.id) return;

			try {
				if (!afterCursor) {
					if (hasInitiallyLoaded.current) {
						setFilterLoading(true);
					} else {
						setLoading(true);
					}
				}
				const result = await client.query({
					query: queryAnnouncements,
					variables: {
						userId: currentUser.id,
						overrideRole: !isModerator || undefined,
						search: searchQuery || null,
						sortBy,
						tags: selectedTags.length > 0 ? selectedTags : null,
						first: BATCH_SIZE,
						after: afterCursor,
					},
					fetchPolicy: 'network-only',
				});
				const data = get(result, 'data.queryAnnouncements', {});
				const items = data.items || [];
				if (!afterCursor) {
					setTotalCount(data.totalCount || 0);
				}
				setNextToken(data.nextToken || null);

				if (afterCursor) {
					setAnnouncements((prev) => [...prev, ...items]);
				} else {
					setAnnouncements(items);
				}
			} catch (error) {
				console.error('Error fetching announcements:', error);
				message.error('Failed to load announcements');
			} finally {
				setLoading(false);
				setFilterLoading(false);
				hasInitiallyLoaded.current = true;
			}
		},
		[client, currentUser?.id, isModerator, searchQuery, sortBy, selectedTags]
	);

	useEffect(() => {
		fetchAnnouncementsNew();
	}, [fetchAnnouncementsNew]);

	// Open announcement from query param (e.g. ?announcementId=xxx)
	const hasOpenedFromParam = useRef(false);
	useEffect(() => {
		if (hasOpenedFromParam.current || announcements.length === 0) return;
		const params = new URLSearchParams(window.location.search);
		const announcementId = params.get('announcementId');
		if (!announcementId) return;

		const match = announcements.find((a) => a.id === announcementId);
		if (match) {
			setSelectedAnnouncement(match);
			setPreloadedThumbnailUrl(match.thumbnailSignedUrl || null);
			hasOpenedFromParam.current = true;
		}
	}, [announcements]);

	// Fetch user groups
	useEffect(() => {
		if (!currentUser?.companyId) return;

		const fetchUserGroups = async () => {
			try {
				const result = await client.query({
					query: QueryCompanyEntitiesUserGroups,
					variables: { companyId: currentUser.companyId },
					fetchPolicy: 'network-only',
				});
				const groups = get(result, 'data.queryCompanyEntities.userGroups', []);
				setUserGroups(groups);
			} catch (error) {
				console.error('Error fetching user groups:', error);
			}
		};

		fetchUserGroups();
	}, [client, currentUser?.companyId]);

	// Fetch announcements settings from Company
	useEffect(() => {
		if (!currentUser?.companyId) return;

		const fetchSettings = async () => {
			try {
				const result = await client.query({
					query: GetCompanyAnnouncementsSettings,
					variables: { id: currentUser.companyId },
					fetchPolicy: 'network-only',
				});
				const settings = get(result, 'data.getCompany.announcementsSettings');
				if (settings) {
					if (settings.headerTitle) setHeaderTitle(settings.headerTitle);
					if (settings.headerSubtext) setHeaderSubtext(settings.headerSubtext);
					if (settings.accentColor) setAccentColor(settings.accentColor);
					if (settings.pinnedColor) setPinnedColor(settings.pinnedColor);
					if (settings.gradientStart) setGradientStart(settings.gradientStart);
					if (settings.gradientMid) setGradientMid(settings.gradientMid);
					if (settings.gradientEnd) setGradientEnd(settings.gradientEnd);
					if (settings.showAuthor !== null && settings.showAuthor !== undefined)
						setShowAuthor(settings.showAuthor);
					if (settings.showViews !== null && settings.showViews !== undefined)
						setShowViews(settings.showViews);
					if (settings.showCount !== null && settings.showCount !== undefined)
						setShowCount(settings.showCount);
					if (settings.defaultAuthor) setDefaultAuthor(settings.defaultAuthor);
				}
			} catch (error) {
				console.error('Error fetching announcements settings:', error);
			} finally {
				setSettingsLoaded(true);
			}
		};

		fetchSettings();
	}, [client, currentUser?.companyId]);

	// Intersection observer for infinite scroll
	const sentinelNodeRef = useRef(null);

	useEffect(() => {
		const node = sentinelNodeRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && nextToken) {
					fetchAnnouncementsNew(nextToken);
				}
			},
			{ threshold: 0.1 }
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [nextToken, fetchAnnouncementsNew]);

	// Fetch all tags from server
	const [allTags, setAllTags] = useState([]);
	const fetchTags = useCallback(async () => {
		if (!currentUser?.companyId) return;
		try {
			const result = await client.query({
				query: QueryAnnouncementsTagsByCompanyId,
				variables: { companyId: currentUser.companyId },
				fetchPolicy: 'network-only',
			});
			const tags = get(result, 'data.queryAnnouncementsTagsByCompanyId', []);
			setAllTags(tags);
		} catch (error) {
			console.error('Error fetching tags:', error);
		}
	}, [client, currentUser?.companyId]);

	useEffect(() => {
		fetchTags();
	}, [fetchTags]);

	// Split pinned/unpinned (server handles filtering and sorting)
	const { pinned, unpinned } = useMemo(() => ({
		pinned: announcements.filter((a) => a.pinned),
		unpinned: announcements.filter((a) => !a.pinned),
	}), [announcements]);

	const visibleUnpinned = unpinned;
	const hasMore = Boolean(nextToken);

	const handleSortChange = useCallback((newSort) => {
		setSortBy(newSort);
	}, []);

	const handleTagToggle = useCallback((tag) => {
		setSelectedTags((prev) =>
			prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
		);
	}, []);

	const handleClearTags = useCallback(() => {
		setSelectedTags([]);
	}, []);

	const handleAddNew = useCallback(() => {
		setEditingAnnouncement(null);
		setIsFormOpen(true);
	}, []);

	const handleOpenSettings = useCallback(() => {
		setIsSettingsOpen(true);
	}, []);

	const handleCloseDetail = useCallback(() => {
		setSelectedAnnouncement(null);
		setPreloadedThumbnailUrl(null);
	}, []);

	const handleCloseForm = useCallback(() => {
		setIsFormOpen(false);
		setEditingAnnouncement(null);
	}, []);

	const handleCloseSettings = useCallback(() => {
		setIsSettingsOpen(false);
	}, []);

	const handleTogglePin = useCallback(
		async (announcement) => {
			try {
				const result = await client.mutate({
					mutation: gql(updateAnnouncement),
					variables: {
						input: {
							id: announcement.id,
							companyId: announcement.companyId,
							title: announcement.title,
							content: announcement.content,
							hide: announcement.hide,
							pinned: !announcement.pinned,
							tags: announcement.tags || [],
						},
					},
				});
				const updated = get(result, 'data.updateAnnouncement');
				if (updated) {
					setAnnouncements((prev) =>
						prev.map((a) => (a.id === updated.id ? updated : a))
					);
					message.success(
						announcement.pinned
							? 'Announcement unpinned'
							: 'Announcement pinned'
					);
				}
			} catch (error) {
				console.error('Error toggling pin:', error);
				message.error('Failed to update announcement');
			}
		},
		[client]
	);

	const handleDelete = useCallback(
		async (announcement) => {
			try {
				await client.mutate({
					mutation: gql(deleteAnnouncement),
					variables: { input: { id: announcement.id } },
				});
				setAnnouncements((prev) =>
					prev.filter((a) => a.id !== announcement.id)
				);
				fetchTags();
				message.success(
					ml('Announcement deleted', currentUser, allMultiLingualData)
				);
			} catch (error) {
				console.error('Error deleting announcement:', error);
				message.error('Failed to delete announcement');
			}
		},
		[client, currentUser, allMultiLingualData, fetchTags]
	);

	const handleSave = useCallback(
		async (data, isEditing) => {
			try {
				if (isEditing) {
					const result = await client.mutate({
						mutation: gql(updateAnnouncement),
						variables: { input: data },
					});
					const updated = get(result, 'data.updateAnnouncement');
					if (updated) {
						setAnnouncements((prev) =>
							prev.map((a) => (a.id === updated.id ? updated : a))
						);
						message.success(
							ml(
								'Announcement updated successfully',
								currentUser,
								allMultiLingualData
							)
						);
					}
				} else {
					const result = await client.mutate({
						mutation: gql(createAnnouncement),
						variables: { input: data },
					});
					const created = get(result, 'data.createAnnouncement');
					if (created) {
						setAnnouncements((prev) => [created, ...prev]);
						message.success(
							ml(
								'Announcement created successfully',
								currentUser,
								allMultiLingualData
							)
						);
					}
				}

				setIsFormOpen(false);
				setEditingAnnouncement(null);

				// Optimistically merge saved tags into allTags immediately,
				// since the backend index may not reflect them yet
				if (data.tags && data.tags.length > 0) {
					setAllTags((prev) =>
						[...new Set([...prev, ...data.tags])].sort()
					);
				}

				fetchTags();
			} catch (error) {
				console.error('Error saving announcement:', error);
				message.error('Failed to save announcement');
			}
		},
		[client, currentUser, allMultiLingualData, fetchTags]
	);

	const handleEdit = useCallback((announcement) => {
		setEditingAnnouncement(announcement);
		setIsFormOpen(true);
		setSelectedAnnouncement(null);
	}, []);

	const handleDeleteTag = useCallback(
		async (tagToDelete) => {
			// Fetch all announcements that have this tag, paginating through all results
			let allMatching = [];
			let cursor = null;
			do {
				const result = await client.query({
					query: queryAnnouncements,
					variables: {
						userId: currentUser.id,
						overrideRole: !isModerator || undefined,
						tags: [tagToDelete],
						first: BATCH_SIZE,
						after: cursor,
					},
					fetchPolicy: 'network-only',
				});
				const data = get(result, 'data.queryAnnouncements', {});
				allMatching = [...allMatching, ...(data.items || [])];
				cursor = data.nextToken || null;
			} while (cursor);

			// Update each announcement to remove the tag, allowing partial success
			const results = await Promise.allSettled(
				allMatching.map((a) =>
					client.mutate({
						mutation: gql(updateAnnouncement),
						variables: {
							input: {
								id: a.id,
								companyId: a.companyId,
								title: a.title,
								content: a.content,
								hide: a.hide,
								pinned: a.pinned,
								tags: (a.tags || []).filter((t) => t !== tagToDelete),
							},
						},
					})
				)
			);

			const failedCount = results.filter((r) => r.status === 'rejected').length;

			// Update local announcements state to reflect successfully removed tags
			const succeededIds = new Set(
				allMatching.filter((_, i) => results[i].status === 'fulfilled').map((a) => a.id)
			);
			setAnnouncements((prev) =>
				prev.map((a) =>
					succeededIds.has(a.id)
						? { ...a, tags: (a.tags || []).filter((t) => t !== tagToDelete) }
						: a
				)
			);

			// Also remove the tag from the filter selection if active
			setSelectedTags((prev) => prev.filter((t) => t !== tagToDelete));

			// Refetch tags from server
			await fetchTags();

			if (failedCount > 0) {
				throw new Error(
					`Failed to remove tag from ${failedCount} announcement${failedCount > 1 ? 's' : ''}. Please try again.`
				);
			}
		},
		[client, currentUser?.id, fetchTags]
	);

	const handleSaveSettings = useCallback(async (settings) => {
		// Apply settings to local state
		setHeaderTitle(settings.headerTitle);
		setHeaderSubtext(settings.headerSubtext);
		setAccentColor(settings.accentColor);
		setPinnedColor(settings.pinnedColor);
		setGradientStart(settings.gradientStart);
		setGradientMid(settings.gradientMid);
		setGradientEnd(settings.gradientEnd);
		setShowAuthor(settings.showAuthor);
		setShowViews(settings.showViews);
		setShowCount(settings.showCount);
		setDefaultAuthor(settings.defaultAuthor);

		// Persist to backend
		try {
			await client.mutate({
				mutation: UpdateCompanyAnnouncementsSettings,
				variables: {
					input: {
						id: currentUser.companyId,
						announcementsSettings: settings,
					},
				},
			});
			message.success(ml('Settings saved', currentUser, allMultiLingualData));
		} catch (error) {
			console.error('Error saving settings:', error);
			message.error('Failed to save settings');
		}
	}, [client, currentUser, allMultiLingualData]);

	const handleIncrementViewCount = useCallback(
		async (announcementId) => {
			try {
				const result = await client.mutate({
					mutation: IncrementAnnouncementViewCount,
					variables: { id: announcementId },
				});
				const updated = get(result, 'data.incrementAnnouncementViewCount');
				if (updated) {
					setAnnouncements((prev) =>
						prev.map((a) =>
							a.id === updated.id ? { ...a, viewCount: updated.viewCount } : a
						)
					);
				}
			} catch (error) {
				console.error('Error incrementing view count:', error);
			}
		},
		[client]
	);


	const handleReadMore = useCallback(
		(announcement, cachedThumbnailUrl) => {
			setPreloadedThumbnailUrl(cachedThumbnailUrl || null);
			setSelectedAnnouncement(announcement);
			handleIncrementViewCount(announcement.id);
		},
		[handleIncrementViewCount]
	);

	if (!currentUser?.company || !settingsLoaded) return <Spinner />;
	if (loading) return <Spinner />;

	return (
		<div className="mx-auto lg:p-8" style={{ maxWidth: 1280, padding: '1px' }}>
			{/* Remove focus outlines from all buttons in this component */}
			<style>{`.announcements-root button:focus { outline: none !important; box-shadow: none !important; } .announcements-root .card-thumbnail-img { transition: transform 0.7s ease-out; } .announcements-root .group:hover .card-thumbnail-img { transform: scale(1.08); } @keyframes gradientShift { from { background-position: 0% 0%; } to { background-position: -200% 0%; } } .announcements-root .group:hover .card-gradient-bar { animation: gradientShift 4s linear infinite !important; } @keyframes cardFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } } .announcement-card-wrapper { animation: cardFadeIn 0.3s ease-out both; }`}</style>
			<div className="announcements-root">
				{/* Optional header block */}
				{(headerTitle || headerSubtext) && (
					<div
						className="mb-6 rounded-2xl"
						style={{ padding: 24, background: '#f8fafc' }}
					>
						{headerTitle && (
							<h1
								style={{
									fontSize: 22,
									fontWeight: 700,
									color: '#1e293b',
									margin: 0,
								}}
							>
								{headerTitle}
							</h1>
						)}
						{headerSubtext && (
							<p
								style={{
									fontSize: 14,
									color: '#475569',
									margin: headerTitle ? '6px 0 0' : 0,
								}}
							>
								{headerSubtext}
							</p>
						)}
					</div>
				)}

				<SearchFilterBar
					searchQuery={searchQuery}
					onSearchChange={setSearchQuery}
					allTags={allTags}
					selectedTags={selectedTags}
					onTagToggle={handleTagToggle}
					onClearTags={handleClearTags}
					sortBy={sortBy}
					onSortChange={handleSortChange}
					isModerator={isModerator}
					onAddNew={handleAddNew}
					onOpenSettings={handleOpenSettings}
					accentColor={accentColor}
					totalResults={totalCount}
					currentUser={currentUser}
					allMultiLingualData={allMultiLingualData}
				/>

				<div
					style={{
						transition: 'opacity 0.1s',
						opacity: filterLoading ? 0.7 : 1,
						pointerEvents: filterLoading ? 'none' : 'auto',
					}}
				>
					{/* Pinned section */}
					{pinned.length > 0 && (
						<section className="mb-10">
							<div
								className="flex items-center gap-2"
								style={{ marginBottom: 20, lineHeight: 1 }}
							>
								<LuPin
									size={14}
									color={darkenHex(pinnedColor)}
									style={{ flexShrink: 0, marginTop: -2 }}
								/>
								<h2
									className="uppercase"
									style={{
										color: '#1e293b',
										letterSpacing: '0.05em',
										fontSize: 14,
										fontWeight: 600,
										margin: 0,
									}}
								>
									{ml('Pinned', currentUser, allMultiLingualData)}
								</h2>
								<div
									className="ml-2"
									style={{ flex: 1, height: 1, background: '#f1f5f9' }}
								/>
							</div>
							<div
								className="grid gap-5"
								style={{
									gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
								}}
							>
								<>
									{pinned.map((a) => (
										<div
											key={a.id}
											className="announcement-card-wrapper"
											style={{ position: 'relative', height: '100%' }}
										>
											<AnnouncementCard
												announcement={a}
												isModerator={isModerator}
												onReadMore={handleReadMore}
												onEdit={handleEdit}
												onDelete={handleDelete}
												onTogglePin={handleTogglePin}
												languageCode={languageCode}
												currentUser={currentUser}
												allMultiLingualData={allMultiLingualData}
												accentColor={accentColor}
												gradientCssLooping={gradientCssLooping}
												pinnedColor={pinnedColor}
												showAuthor={showAuthor}
												showViews={showViews}
												defaultAuthor={defaultAuthor}
											/>
										</div>
									))}
								</>
							</div>
						</section>
					)}

					{/* Recent / unpinned section */}
					<section>
						{pinned.length > 0 && unpinned.length > 0 && (
							<div
								className="flex items-center gap-2"
								style={{ marginBottom: 20 }}
							>
								<h2
									className="uppercase"
									style={{
										color: '#1e293b',
										letterSpacing: '0.05em',
										fontSize: 14,
										fontWeight: 600,
										margin: 0,
									}}
								>
									{ml('Recent', currentUser, allMultiLingualData)}
								</h2>
								<div
									className="ml-2"
									style={{ flex: 1, height: 1, background: '#f1f5f9' }}
								/>
							</div>
						)}

						{visibleUnpinned.length > 0 ? (
							<div
								className="grid gap-5"
								style={{
									gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
								}}
							>
								<>
									{visibleUnpinned.map((a) => (
										<div
											key={a.id}
											className="announcement-card-wrapper"
											style={{ position: 'relative', height: '100%' }}
										>
											<AnnouncementCard
												announcement={a}
												isModerator={isModerator}
												onReadMore={handleReadMore}
												onEdit={handleEdit}
												onDelete={handleDelete}
												onTogglePin={handleTogglePin}
												languageCode={languageCode}
												currentUser={currentUser}
												allMultiLingualData={allMultiLingualData}
												accentColor={accentColor}
												gradientCssLooping={gradientCssLooping}
												pinnedColor={pinnedColor}
												showAuthor={showAuthor}
												showViews={showViews}
												defaultAuthor={defaultAuthor}
											/>
										</div>
									))}
								</>
							</div>
						) : (
							pinned.length === 0 && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									className="flex flex-col items-center justify-center rounded-2xl py-20"
									style={{ background: '#f8fafc' }}
								>
									<div
										className="mb-4 flex items-center justify-center rounded-2xl"
										style={{ width: 64, height: 64, background: '#f1f5f9' }}
									>
										<LuPin size={28} color="#cbd5e1" />
									</div>
									<p className="font-medium" style={{ color: '#475569' }}>
										{ml(
											'No announcements found',
											currentUser,
											allMultiLingualData
										)}
									</p>
									<p className="mt-1 text-sm" style={{ color: '#475569' }}>
										{ml(
											'Try adjusting your search or filters',
											currentUser,
											allMultiLingualData
										)}
									</p>
								</motion.div>
							)
						)}

						{/* Infinite scroll sentinel */}
						{hasMore && (
							<div
								ref={sentinelNodeRef}
								className="flex items-center justify-center py-10"
							>
								<Spin size="small" />
								<span className="ml-2 text-sm" style={{ color: '#475569' }}>
									{ml('Loading more...', currentUser, allMultiLingualData)}
								</span>
							</div>
						)}
					</section>
				</div>

				{/* Detail Modal */}
				<AnimatePresence>
					{selectedAnnouncement && (
						<AnnouncementDetail
							announcement={selectedAnnouncement}
							isModerator={isModerator}
							onClose={handleCloseDetail}
							onEdit={handleEdit}
							onDelete={handleDelete}
							languageCode={languageCode}
							currentUser={currentUser}
							allMultiLingualData={allMultiLingualData}
							preloadedThumbnailUrl={preloadedThumbnailUrl}
							showAuthor={showAuthor}
							showViews={showViews}
							defaultAuthor={defaultAuthor}
							pinnedColor={pinnedColor}
						/>
					)}
				</AnimatePresence>

				{/* Form Modal */}
				<AnimatePresence>
					{isFormOpen && (
						<AnnouncementForm
							announcement={editingAnnouncement}
							allTags={allTags}
							onSave={handleSave}
							onClose={handleCloseForm}
							onDeleteTag={handleDeleteTag}
							currentUser={currentUser}
							allMultiLingualData={allMultiLingualData}
							userGroups={userGroups}
							accentColor={accentColor}
						/>
					)}
				</AnimatePresence>

				{/* Settings Modal */}
				<AnimatePresence>
					{isSettingsOpen && (
						<SettingsModal
							headerTitle={headerTitle}
							headerSubtext={headerSubtext}
							accentColor={accentColor}
							pinnedColor={pinnedColor}
							gradientStart={gradientStart}
							gradientMid={gradientMid}
							gradientEnd={gradientEnd}
							showAuthor={showAuthor}
							showViews={showViews}
							showCount={showCount}
							defaultAuthor={defaultAuthor}
							onSave={handleSaveSettings}
							onClose={handleCloseSettings}
							currentUser={currentUser}
							allMultiLingualData={allMultiLingualData}
						/>
					)}
				</AnimatePresence>
			</div>
		</div>
	);
}

export default NewAnnouncementsComponent;
